import { Autocomplete, GridLegacy as Grid, TextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { Controller, FieldError, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmployeeSelect } from '~/components/GenericForm/EmployeeSelect';
import { CostCenter } from '~/data/costCenters';
import { EmpPosition } from '~/data/employee/types';
import { EmployeesIndexed } from '~/data/employees';
import { AllEnums } from '~/data/enums';
import { LegalEntity } from '~/data/legalEntity';
import { OrgTreeData, OrganizationUnit } from '~/data/orgunits/types';
import { UnitPosition } from '~/data/unitPositions';
import { resetEnumsFormValues } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  CostCenterComponent,
  InternalPositionComponent,
  ManagerComponent,
  UnitPositionComponent,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components';
import { ScheduledEmploymentUnitSelection } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import {
  ChangedStartDateState,
  Form,
  Restrict,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  index: number;
  empData: EmpPosition;
  legalEntities: LegalEntity[];
  allEnums: AllEnums;
  unitPositions: UnitPosition[];
  unitSelection: ScheduledEmploymentUnitSelection;
  managers: number[];
  allEmployees: EmployeesIndexed;
  orgTree: OrgTreeData[];
  restrictField: Restrict;

  newEmpl: boolean;
  allResponsibleHRIds: number[];
  pendingProposal: boolean;
  inProgressPropStartDate?: number;
  hrSelectorDisable: boolean;
  newEngine: boolean;

  getCostCenterValues: () => CostCenter[];
  onUnitChange: (newValue: number) => void;
  onLegalEntityChange: (newValue: string) => void;
  setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  empPositionPending?: EmpPosition;
};

export const FormComponent = (props: Props) => {
  const {
    scheduleFormMethods,
    getCostCenterValues,
    empData,
    legalEntities,
    allEnums,
    onUnitChange,
    unitPositions,
    unitSelection,
    managers,
    allEmployees,
    onLegalEntityChange,
    setWarningOpen,
    orgTree,
    restrictField,
    index,
    newEmpl,
    setChangedStartDate,
    allResponsibleHRIds,
    pendingProposal,
    inProgressPropStartDate,
    hrSelectorDisable,
    newEngine,
    empPositionPending,
  } = props;

  const { getValues, control } = scheduleFormMethods;
  const { t } = useTranslation();
  const requiredText = t('fieldIsRequired');

  const getTextFieldProps = useCallback(
    (
      props: { error?: FieldError } & Partial<Omit<TextFieldProps, 'error'>>,
    ): Partial<TextFieldProps> => {
      const { error, slotProps, ...rest } = props;

      return {
        size: 'small',
        error: !!error,
        helperText: error?.message,
        slotProps: {
          ...slotProps,
          inputLabel: {
            shrink: true,
            ...slotProps?.inputLabel,
          },
        },
        ...rest,
      };
    },
    [],
  );

  const startDateMinDate = useMemo((): Date | undefined => {
    const employmentStartDate = getValues(
      `schedule.${index}.employment.fEmplDateStart`,
    );
    const inProgressStartDate = !!inProgressPropStartDate
      ? moment(inProgressPropStartDate).toDate()
      : undefined;

    if (inProgressStartDate && employmentStartDate) {
      return moment(inProgressStartDate).isBefore(employmentStartDate)
        ? employmentStartDate
        : inProgressStartDate;
    } else if (!inProgressStartDate && employmentStartDate) {
      return employmentStartDate;
    } else if (!employmentStartDate && inProgressStartDate)
      return inProgressStartDate;
    else return undefined;
  }, [getValues, inProgressPropStartDate, index]);

  const openWarning = useCallback(
    (v: Date | null) => {
      if (!v) {
        return;
      }
      if (moment(v).isBefore(new Date())) {
        setWarningOpen(true);
      }
    },
    [setWarningOpen],
  );

  const DEBOUNCE_TIMER_MS = 400;

  const onInternalPositionStartDateChangeSync = useMemo(
    () =>
      debounce((v: Date | null) => {
        openWarning(v);
        setChangedStartDate((activeValue) => ({
          ...activeValue,
          empPositionStartDate: true,
        }));
      }, DEBOUNCE_TIMER_MS),
    [openWarning, setChangedStartDate],
  );

  const BusinessUnits = useMemo(
    () => (
      <>
        {/* INFO: The first is the main org tree, that falls under UNIT not business unit */}
        {orgTree.slice(1).map((bu) => {
          return (
            <Grid item key={bu.fTreeId} xs={12} sm={6} md={4} lg={3}>
              <Controller
                control={control}
                name={`schedule.${index}.fEmpBusinessUnit${bu.fTreeId}`}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    options={bu.units}
                    getOptionLabel={(o) => o.fTreeUnitName}
                    isOptionEqualToValue={(o, v) =>
                      o.fTreeUnitId === v.fTreeUnitId
                    }
                    onChange={(_, value) => onChange(value?.fTreeUnitId || '')}
                    value={
                      bu.units.find((o) => o.fTreeUnitId === value) || null
                    }
                    size={'small'}
                    data-testid={`business-unit-${bu.fTreeId}-autocomplete`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...getTextFieldProps({ error, label: bu.fTreeName })}
                        data-testid={`business-unit-${bu.fTreeId}-input`}
                      />
                    )}
                    fullWidth={true}
                  />
                )}
              />
            </Grid>
          );
        })}
      </>
    ),
    [control, index, orgTree, getTextFieldProps],
  );

  const { unitOptions, groupToMyUnitsAndOthers } = useMemo(() => {
    const optData = unitSelection.options;
    const myUnitIds = optData.myUnits.map((u) => u.fOrgUnitId);

    const groupToMyUnitsAndOthers = (option: unknown) => {
      const opt = option as OrganizationUnit;

      if (optData.allUnitOptionsEnabled) {
        return myUnitIds.includes(opt.fOrgUnitId)
          ? t('myUnits')
          : t('otherUnits');
      }

      return '';
    };

    const unitOptions = optData.allUnitOptionsEnabled
      ? [...optData.myUnits, ...optData.otherUnits]
      : optData.myUnits;

    return {
      unitOptions,
      groupToMyUnitsAndOthers,
    };
  }, [unitSelection.options, t]);

  return (
    <Grid container columnSpacing={2} rowSpacing={2}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fEmpLegalEntity`}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              disabled={restrictField.manager && !restrictField.hr}
              placeholder={' '}
              options={legalEntities}
              getOptionLabel={(c) =>
                `${c.fLegalEntityName} (${c.fLegalEntityCode})`
              }
              onChange={(_, value) => {
                const fLegalEntityId = value?.fLegalEntityId.toString() || '';
                onChange(fLegalEntityId);
                onLegalEntityChange(fLegalEntityId);
                resetEnumsFormValues(
                  fLegalEntityId,
                  getValues(
                    `schedule.${index}.empPosition.fInternalPositionUnit`,
                  ),
                  allEnums,
                  index,
                  scheduleFormMethods,
                  legalEntities,
                );
              }}
              value={
                legalEntities.find(
                  (le) => le.fLegalEntityId.toString() === value,
                ) || null
              }
              size={'small'}
              data-testid="legal-entity-autocomplete"
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...getTextFieldProps({
                    error,
                    label: t('fEmpLegalEntity'),
                    required: true,
                  })}
                  data-testid="legal-entity-input"
                />
              )}
              fullWidth={true}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InternalPositionComponent
          index={index}
          control={control}
          getValues={getValues}
          allEnums={allEnums}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fEmpJobTitle`}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              value={value}
              onChange={onChange}
              label={t('fEmpJobTitle')}
              fullWidth={true}
              data-testid="job-title-input"
              {...getTextFieldProps({ error })}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fInternalPositionInfo`}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              inputProps={{ maxLength: 80 }}
              value={value}
              onChange={onChange}
              label={t('laInfo')}
              fullWidth={true}
              data-testid="int-position-info-input"
              {...getTextFieldProps({ error })}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          control={control}
          name={`schedule.${index}.empPosition.fInternalPositionUnit`}
          rules={{
            validate: (value) => (value && value !== -1) || requiredText,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              placeholder={''}
              options={unitOptions}
              groupBy={groupToMyUnitsAndOthers}
              getOptionLabel={(c) => `${c.fOrgUnitName}, ${c.fOrgUnitNumber}`}
              onChange={(_, value) => {
                onChange(value?.fOrgUnitId || -1);
                onUnitChange(value?.fOrgUnitId || -1);
                resetEnumsFormValues(
                  getValues(`schedule.${index}.empPosition.fEmpLegalEntity`),
                  value?.fOrgUnitId || -1,
                  allEnums,
                  index,
                  scheduleFormMethods,
                  legalEntities,
                );
              }}
              value={
                unitOptions.find((units) => units.fOrgUnitId === value) || null
              }
              size={'small'}
              data-testid="int-position-unit-autocomplete"
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...getTextFieldProps({
                    error,
                    label: t('laUnit'),
                    required: true,
                  })}
                  data-testid="int-position-unit-input"
                />
              )}
              fullWidth={true}
            />
          )}
        />
      </Grid>
      {index === 0 && <>{BusinessUnits}</>}
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <ManagerComponent
          index={index}
          control={control}
          managers={managers}
          allEmployees={allEmployees}
        />
      </Grid>
      <Grid item xs={12}>
        <UnitPositionComponent
          index={index}
          unitPositions={unitPositions}
          empData={empData}
        />
      </Grid>
      <Grid item xs={12}>
        <CostCenterComponent
          index={index}
          getCostCenterValues={getCostCenterValues}
          empPositionPending={empPositionPending}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Controller
          name={`schedule.${index}.empPosition.fInternalPositionStartDate`}
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const minDate: Date | undefined = startDateMinDate;
            const maxDate: Date | undefined =
              getValues(
                `schedule.${index}.employment.fEmplLastDateEmployment`,
              ) || undefined;

            return (
              <DatePicker
                disabled={newEmpl || pendingProposal}
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange: (value: Date | null, context) => {
                    onChange(value, context);
                    onInternalPositionStartDateChangeSync(value);
                  },
                  minDate,
                  maxDate,
                })}
                label={t('laStartDate')}
                slotProps={{
                  textField: getTextFieldProps({
                    error,
                    required: true,
                  }),
                }}
              />
            );
          }}
        />
      </Grid>
      {index === 0 && newEngine && (
        <Grid item xs={12} md={6}>
          <Controller
            name={`schedule.${index}.empPosition.fInternalPositionProposalHrId`}
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EmployeeSelect
                  FormControlProps={{ fullWidth: true, error: !!error }}
                  label={t('laHRResponsible')}
                  value={value || ''}
                  onChange={onChange}
                  optionEmployeeIds={allResponsibleHRIds}
                  fullWidth={true}
                  required={true}
                  disabled={pendingProposal || hrSelectorDisable}
                  size="small"
                />
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
